/**
 * Ozonetel-only automatic warm-transfer configuration.
 *
 * Source: helper/dialersConfigs/auto-call-transfer-config.json (ozonetel section).
 * Toggle AUTO_CALL_TRANSFER_USE_S3 in auto-call-transfer-config.ts for remote config later.
 *
 * Each campaign has its own dummyUsers (source) and activeUsers (destination).
 * Only dialerUser values are used; moneytorUser is ignored.
 *
 * Flow:
 * 1) Module load → global config loader → applyConfig() builds in-memory campaign rule map.
 * 2) calling.component.maybeAutoTransferConnectedCall() on CONNECTED:
 *    - resolve campaignName from call data
 *    - isAutoTransferEligibleCampaign(campaignName)
 *    - isOzonetelAutoTransferDummyAgent(agentId, campaignName) — that campaign's dummyUsers only
 *    - pickOzonetelAutoTransferTarget(onlineIds, campaignName) — that campaign's activeUsers only
 */

import {
  AUTO_CALL_TRANSFER_USE_S3,
  extractDialerUsers,
  getOzonetelCampaignsConfig,
  initAutoCallTransferConfig,
} from '../../../helper/dialersConfigs/auto-call-transfer-config';

/** Per-campaign source and destination agent lists. */
export interface OzonetelCampaignTransferRule {
  dummyUsers: string[];
  activeUsers: string[];
}

/** Shape of the S3 / reference JSON config. */
export interface OzonetelAutoTransferConfig {
  campaigns: Record<string, OzonetelCampaignTransferRule>;
}

interface StoredCampaignRule {
  campaignName: string;
  rule: OzonetelCampaignTransferRule;
}

const campaignRules = new Map<string, StoredCampaignRule>();

function loadOzonetelConfig(): void {
  applyConfig(getOzonetelCampaignsConfig());
}

loadOzonetelConfig();
void initAutoCallTransferConfig().then(() => {
  if (AUTO_CALL_TRANSFER_USE_S3) {
    loadOzonetelConfig();
  }
});

/** Case-insensitive agent/campaign id comparison (trim + lowercase). */
function normalizeId(value: string): string {
  return String(value ?? '').trim().toLowerCase();
}

function normalizeUserList(value: unknown): string[] {
  return extractDialerUsers(value);
}

/**
 * Resolves the transfer rule for a campaign name (case-insensitive).
 * Returns null when campaign is missing, blank, or not configured in S3.
 */
function resolveCampaignRule(campaignName: string): StoredCampaignRule | null {
  const key = normalizeId(campaignName);
  if (!key) {
    return null;
  }
  return campaignRules.get(key) || null;
}

function applyConfig(raw: unknown): void {
  campaignRules.clear();
  const config = raw as Partial<OzonetelAutoTransferConfig> | null | undefined;
  const campaigns = config?.campaigns;
  if (!campaigns || typeof campaigns !== 'object' || Array.isArray(campaigns)) {
    return;
  }
  for (const [campaignName, entry] of Object.entries(campaigns)) {
    const name = String(campaignName ?? '').trim();
    if (!name) {
      continue;
    }
    const rule: OzonetelCampaignTransferRule = {
      dummyUsers: normalizeUserList(entry?.dummyUsers),
      activeUsers: normalizeUserList(entry?.activeUsers),
    };
    if (rule.dummyUsers.length === 0 && rule.activeUsers.length === 0) {
      continue;
    }
    campaignRules.set(normalizeId(name), { campaignName: name, rule });
  }
}

/**
 * Picks a destination from activeUsers for one campaign.
 * Only returns an agent that is online in onlineAgentIds; null when none are online.
 */
function pickFromActiveUsers(activeUsers: string[], onlineAgentIds?: string[]): string | null {
  const active = activeUsers.map((id) => String(id ?? '').trim()).filter(Boolean);
  if (active.length === 0) {
    return null;
  }
  const onlineSet = new Set(
    (onlineAgentIds || []).map((id) => normalizeId(id)).filter(Boolean)
  );
  if (onlineSet.size === 0) {
    return null;
  }
  const onlineActive = active.filter((id) => onlineSet.has(normalizeId(id)));
  if (onlineActive.length === 0) {
    return null;
  }
  const randomCandidate = onlineActive[Math.floor(Math.random() * onlineActive.length)];
  console.log('pickFromActiveUsers randomCandidate ===================>', randomCandidate);
  return randomCandidate || null;
}

export function getOzonetelAutoTransferCampaigns(): readonly string[] {
  return Array.from(campaignRules.values()).map((entry) => entry.campaignName);
}

/**
 * True when at least one campaign has non-empty dummyUsers and activeUsers.
 * Master switch — calling.component skips auto-transfer when false.
 */
export function isOzonetelAutoTransferConfigReady(): boolean {
  for (const { rule } of campaignRules.values()) {
    if (rule.dummyUsers.length > 0 && rule.activeUsers.length > 0) {
      return true;
    }
  }
  return false;
}

/**
 * True when agentId is in dummyUsers for the given campaign only (case-insensitive).
 * Strict: unknown or unconfigured campaign → false. No cross-campaign fallback.
 */
export function isOzonetelAutoTransferDummyAgent(agentId: string, campaignName: string): boolean {
  const needle = normalizeId(agentId);
  if (!needle) {
    return false;
  }
  const stored = resolveCampaignRule(campaignName);
  if (!stored || stored.rule.dummyUsers.length === 0) {
    return false;
  }
  return stored.rule.dummyUsers.some((id) => normalizeId(id) === needle);
}

/**
 * Picks warm-transfer destination from activeUsers for the given campaign only.
 * Strict: unknown campaign or empty activeUsers → null. No cross-campaign fallback.
 *
 * @param onlineAgentIds — Ozonetel transfer-list agent ids (AgentId) currently online
 * @param campaignName — resolved CONNECTED call campaign name
 */
export function pickOzonetelAutoTransferTarget(
  onlineAgentIds: string[] | undefined,
  campaignName: string
): string | null {
  const stored = resolveCampaignRule(campaignName);
  if (!stored) {
    return null;
  }
  return pickFromActiveUsers(stored.rule.activeUsers, onlineAgentIds);
}
